import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

// ── Context ───────────────────────────────────────────────────────────────────

interface TabsCtx {
  value: string;
  onChange: (v: string) => void;
}

const TabsContext = createContext<TabsCtx | null>(null);

function useTabsContext() {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tab/TabPanel must be used inside <Tabs>');
  return ctx;
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

interface TabsProps {
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}

export function Tabs({ value, onChange, children }: TabsProps) {
  return (
    <TabsContext.Provider value={{ value, onChange }}>
      <div>{children}</div>
    </TabsContext.Provider>
  );
}

// ── TabList ───────────────────────────────────────────────────────────────────

interface TabListProps {
  children: ReactNode;
}

export function TabList({ children }: TabListProps) {
  return (
    <div className="flex gap-1 border-b border-border mb-4" role="tablist">
      {children}
    </div>
  );
}

// ── Tab ───────────────────────────────────────────────────────────────────────

interface TabProps {
  value: string;
  children: ReactNode;
}

export function Tab({ value, children }: TabProps) {
  const { value: active, onChange } = useTabsContext();
  const isActive = active === value;

  return (
    <button
      role="tab"
      aria-selected={isActive}
      onClick={() => onChange(value)}
      className={
        isActive
          ? 'border-b-2 border-primary-500 text-text-primary -mb-px pb-2 px-3 text-sm font-medium transition-colors duration-150 focus:outline-none'
          : 'text-text-secondary hover:text-text-primary pb-2 px-3 text-sm font-medium cursor-pointer transition-colors duration-150 focus:outline-none'
      }
    >
      {children}
    </button>
  );
}

// ── TabPanel ──────────────────────────────────────────────────────────────────

interface TabPanelProps {
  value: string;
  children: ReactNode;
}

export function TabPanel({ value, children }: TabPanelProps) {
  const { value: active } = useTabsContext();
  if (active !== value) return null;
  return <div role="tabpanel">{children}</div>;
}
